import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import {
  getSavedListCapacityDialogContent,
  openConstraintAction,
} from '@/entities/workspace/util/capacity-constraint-ux';
import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { getInfluencerDiscoveryCapacityError } from '@/shared/utils';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';
import type { AddCreatorRequestDTO, RemoveCreatorRequestDTO } from '../dto';

import { trackEvent } from '@/shared/track';
import { showDialog } from '@/shared/utils/show-dialog';
import {
  AddCreatorsToSavedList,
  CreateSavedList,
  DeleteSavedList,
  RemoveCreatorsFromSavedList,
  UpdateSavedListTitle,
} from '..';

export const useCreateProfileList = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useMutation({
    mutationFn: (title: string) =>
      CreateSavedList({
        title,
        workspace_id: workspaceId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.all() });
    },
    onError: async (error) => {
      const capacityError = await getInfluencerDiscoveryCapacityError(error);

      if (capacityError?.message && workspace) {
        const feedback = getSavedListCapacityDialogContent(workspace.plan, workspaceId);
        const action = feedback.action;
        showDialog({
          title: feedback.title,
          message: feedback.description,
          confirmLabel: action ? action.label : undefined,
          onConfirm: action
            ? () =>
                openConstraintAction(action, {
                  navigateInternal: (href) => navigate({ to: href }),
                })
            : undefined,
        });
        trackEvent('Saved_list', 'create_list_failed', {
          reason: 'saved_list_capacity_exceeded',
        });
        return;
      }

      trackEvent('Saved_list', 'create_list_failed');
      await showErrorDialog({
        error,
        title: 'Failed to create saved list',
      });
    },
  });
};

export const useUpdateProfileList = () => {
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useMutation({
    mutationFn: ({ listId, title }: { listId: string; title: string }) =>
      UpdateSavedListTitle(listId, title),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.detail(listId) });
    },
  });
};

export const useDeleteProfileList = () => {
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useMutation({
    mutationFn: (listId: string) => DeleteSavedList(listId),
    onSuccess: () => {
      trackEvent('Saved_list', 'delete_list');
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.all() });
    },
  });
};

export const useAddProfilesToList = () => {
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useMutation({
    mutationFn: (params: { listId: string; request: AddCreatorRequestDTO }) =>
      AddCreatorsToSavedList(params.listId, params.request),
    onSuccess: (_, { listId }) => {
      trackEvent('Saved_list', 'add_creators');
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.detail(listId) });
    },
  });
};

export const useRemoveProfilesFromList = () => {
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();

  return useMutation({
    mutationFn: (params: { listId: string; request: RemoveCreatorRequestDTO }) =>
      RemoveCreatorsFromSavedList(params.listId, params.request),
    onSuccess: (_, { listId }) => {
      trackEvent('Saved_list', 'remove_creators');
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.savedCreatorsList.detail(listId) });
    },
  });
};
